/*  ───────────────────────────────────────────────────────────────
    src/views/admin/settings/role-permissions/role/RoleUpdatePage.jsx
    (adds “manage” column + handles manage_leave, keeps ALL debug)
└──────────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from 'react';
import { useParams }   from 'react-router-dom';
import { useDispatch } from 'react-redux';

import Card        from '@/components/ui/cards/Card';
import CardHeader  from '@/components/ui/cards/CardHeader';
import CardBody    from '@/components/ui/cards/CardBody';
import InputCheck  from '@/components/ui/inputs/input-check';

import { getPermissionService } from '@/services/PermissionServices';
import {
  getByIdRoleService,
  updateRoleService,
} from '@/services/RoleServices';
import validationErrors from '@/lib/validationErrors';

/* ─── helpers ────────────────────────────────────────────────── */
// new ACTION order → create / update / delete / manage / view / view_all
const ACTIONS = ['create', 'update', 'delete', 'manage', 'view', 'view_all'];

/**
 * view_users_all → view_all
 * request_leave  → create
 * manage_leave   → manage   (NEW)
 */
const extractAction = (val = '') => {
  console.debug('[extractAction] input:', val);

  if (val.startsWith('request_')) {
    console.debug('[extractAction] treated as create');
    return 'create';
  }
  if (val.startsWith('manage_')) {
    console.debug('[extractAction] treated as manage');
    return 'manage';
  }

  const parts = val.split('_');
  if (parts[0] === 'view' && parts.at(-1) === 'all') {
    console.debug('[extractAction] treated as view_all');
    return 'view_all';
  }

  console.debug('[extractAction] default action =', parts[0]);
  return parts[0];
};

/**
 * Canonicalise page names, e.g. “Attendance (All)” → “Attendance”
 */
const canonicalPageName = name => {
  const cleaned = name
    .replace(/^view\s+my\s+/i, '')
    .replace(/\s*\(all\)$/i, '')
    .trim();
  console.debug('[canonicalPageName]', name, '→', cleaned);
  return cleaned;
};

/* ─── component ──────────────────────────────────────────────── */
export default function RoleUpdatePage() {
  const { id }   = useParams();
  const dispatch = useDispatch();

  const [permissions, setPermissions] = useState([]);  // raw list
  const [rows,        setRows]        = useState([]);  // processed table rows
  const [role,        setRole]        = useState({});
  const [selected,    setSelected]    = useState([]);  // slugs
  const [busy,        setBusy]        = useState(false);

  console.debug('[RoleUpdatePage] mounted, role id =', id);

  /* ─── fetch perms + role once ─────────────────────────────── */
  useEffect(() => {
    if (!id) return;
    setBusy(true);

    Promise.all([
      dispatch(getPermissionService({ page: 1, limit: 1000 })),
      dispatch(getByIdRoleService(id)),
    ])
      .then(([permRes, roleRes]) => {
        const perms = permRes?.data?.data?.data ?? [];
        const rl    = roleRes?.data?.data ?? {};
        console.debug('[fetch] raw permissions:', perms);
        console.debug('[fetch] fetched role object:', rl);
        setPermissions(perms);
        setRole(rl);
      })
      .catch(err => {
        console.error('[fetch] error:', err);
        validationErrors(err?.errorData, err?.message);
      })
      .finally(() => setBusy(false));
  }, [id, dispatch]);

  /* ─── build table rows whenever permissions change ─────────── */
  useEffect(() => {
    if (!permissions.length) return;

    const byPage = {};
    permissions.forEach(p => {
      const page   = canonicalPageName(p.name);
      const action = extractAction(p.value);

      if (!byPage[page]) byPage[page] = { page, actions: {} };
      byPage[page].actions[action] = p.value;
      console.debug(`[row-build] page=${page}, action=${action}, value=${p.value}`);
    });

    const ordered = Object.values(byPage).sort((a, b) =>
      a.page.localeCompare(b.page)
    );
    console.debug('[row-build] final rows:', ordered);
    setRows(ordered);
  }, [permissions]);

  /* ─── initialise checked boxes from role.permission_ids ───── */
  useEffect(() => {
    if (!role.permission_ids?.length || !permissions.length) return;

    const id2val = new Map(permissions.map(p => [String(p._id), p.value]));
    console.debug('[init-select] id→value map:', id2val);

    const list = role.permission_ids
      .map(x =>
        typeof x === 'object' && x.value
          ? x.value
          : id2val.get(String(x))
      )
      .filter(Boolean);

    console.debug('[init-select] selected slugs:', list);
    setSelected(list);
  }, [role, permissions]);

  /* ─── handlers ─────────────────────────────────────────────── */
  const toggle = slug => {
    const next = selected.includes(slug)
      ? selected.filter(v => v !== slug)
      : [...selected, slug];
    console.debug('[toggle] slug:', slug, '→ next selected:', next);
    setSelected(next);
  };

  const save = e => {
    e.preventDefault();

    const val2id = new Map(permissions.map(p => [p.value, p._id]));
    const permission_ids = selected
      .map(v => val2id.get(v))
      .filter(Boolean);

    console.debug('[save] payload permission_ids:', permission_ids);

    setBusy(true);
    dispatch(updateRoleService(role._id, { name: role.name, permission_ids }))
      .then(() => {
        console.debug('[save] updateRoleService succeeded');
      })
      .catch(err => {
        console.error('[save] error:', err);
        validationErrors(err?.errorData, err?.message);
      })
      .finally(() => setBusy(false));
  };

  /* ─── render ───────────────────────────────────────────────── */
  return (
    <form onSubmit={save}>
      <Card>
        <CardHeader isBorder>
          <h3 className="db-card-title">
            Role & Permissions{' '}
            <span className="text-primary">
              ({role.name || '…'})
            </span>
          </h3>
        </CardHeader>

        <CardBody className="p-0">
          <div className="db-table-responsive">
            <table className="db-table stripe">
              <thead className="db-table-head">
                <tr className="db-table-head-tr">
                  <th className="db-table-head-th">#</th>
                  <th className="db-table-head-th">Page</th>
                  <th className="db-table-head-th">create</th>
                  <th className="db-table-head-th">update</th>
                  <th className="db-table-head-th">delete</th>
                  <th className="db-table-head-th">manage</th>{/* NEW */}
                  <th className="db-table-head-th">view</th>
                  <th className="db-table-head-th">view&nbsp;(all)</th>
                </tr>
              </thead>

              <tbody className="db-table-body">
                {rows.map((row, idx) => (
                  <tr key={row.page} className="db-table-body-tr">
                    <td className="db-table-body-td text-center">{idx + 1}</td>
                    <td className="db-table-body-td text-base capitalize">
                      {row.page}
                    </td>
                    {ACTIONS.map(act => (
                      <td key={act} className="db-table-body-td text-sm text-center">
                        {row.actions[act] && (
                          <InputCheck
                            id={row.actions[act]}
                            checked={selected.includes(row.actions[act])}
                            onChange={() => toggle(row.actions[act])}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>

        <CardBody>
          <button
            type="submit"
            disabled={busy}
            className="db-btn py-2 text-white bg-primary"
          >
            <i className="lab lab-save" />&nbsp;
            {busy ? 'Saving…' : 'Save'}
          </button>
        </CardBody>
      </Card>
    </form>
  );
}
